import './registeraction.scss'

import {Form, Formik} from 'formik'
import {Mod, style} from 'util/campagne/style'
import {Popup, PopupContent} from '../../ui/Popup'
import React, {FunctionComponent, useRef, useState} from 'react'
import {fromServerDate, toServerDate} from 'util/dates'
import {mergeValues, serializeValues} from 'util/values'

import {ActionForm} from 'views/campagne/partials/forms/actionform'
import {ButtonPrimary} from 'views/ui/Button'
import {Layout} from 'views/campagne/layout'
import {PosterForm} from 'views/campagne/partials/forms/posterform'
import {RegisterFormType} from 'campagne/types'
import {Theme} from 'views/campagne/theme'
import {fetch} from 'yetch'
import jsonToFormData from '@ajoelp/json-to-formdata'
import queryString from 'query-string'
import {saveSvgAsPngBlob} from 'util/savesvg'
import {useHistory} from 'react-router'
import {useTranslation} from 'util/i18n'
import {wrap} from '@codeurs/front'

export const initialValues = {
	type: 'school',

	school_name: null,
	school_location: null,

	date_start: null,
	date_end: null,
	date_has_multiple_days: false,
	date_has_end_hour: false,

	hour_start: null,
	hour_end: null,

	location_name: null,
	location_street: null,
	location_number: null,
	location_box: null,
	location_zip: null,
	location_city: null,

	latitude: null,
	longitude: null,

	location_no_address: false,
	private: false,

	organisation_name: null,

	organisator_firstname: null,
	organisator_lastname: null,
	organisator_street: null,
	organisator_number: null,
	organisator_bus: null,
	organisator_postal_code: null,
	organisator_city: null,
	organisator_phone: null,
	organisator_contact_details: null,
	organisator_date_of_birth: null,
	organisator_gender: null,

	contactperson_different_from_organisator: null,
	contactperson_firstname: null,
	contactperson_lastname: null,
	contactperson_street: null,
	contactperson_number: null,
	contactperson_bus: null,
	contactperson_postal_code: null,
	contactperson_city: null,
	contactperson_phone: null,
	contactperson_contact_details: null,
	contactperson_date_of_birth: null,
	contactperson_gender: null,

	subscription_required: false,
	subscription_details: null,

	description_short: null,
	description_long: null,

	external_url: null,

	keywords: null,

	affiche_with: null,
	affiche_what: null,
	affiche_where: null
}

export type FormValues = typeof initialValues

type DiscardType = {
	title: string
	intro: string
	label: string
	label_help: string
	placeholder: string
	back_button: string
	confirm_button: string
}
const styles = style('registeraction', ['form', 'section', 'popupcancel'])

export const RegisterAction: FunctionComponent = wrap(Layout)(
	({form, form_ordened}) => {
		return (
			<div class={styles}>
				<RegisterActionForm orderedForm={form_ordened} {...(form as any)} />
			</div>
		)
	}
)

export const RegisterActionForm: FunctionComponent<
	RegisterFormType &
		typeof initialValues & {
			editing: string
			exportUrl: string
			id: number
			orderedForm: RegisterFormType
			totalAmount: number
			discard: DiscardType
		} & any
> = ({editing, exportUrl, id, orderedForm, discard, totalAmount, ...form}) => {
	const posterRef = useRef()
	const [submitting, setSubmitting] = useState(false)
	const [cancelPopupState, setCancelPopupState] = useState(false)
	const t = useTranslation('campagne').register
	const history = useHistory()
	const endpoint = !editing ? '/api/event' : `/api/event/${id}?token=${editing}`
	const params = queryString.parse(location.search)
	const cancelAction = (message) => {
		fetch(`/api/event/cancel/${id}?token=${editing}`, {
			method: 'post',
			body: jsonToFormData({
				message: message
			})
		})
			.then((res) => {
				switch (res.status) {
					case 200:
						res.json().then(({next}) => history.push(next))
					default:
						console.error(res)
				}
			})
			.catch(console.error)
	}

	return (
		<Theme.Container>
			<Formik
				onSubmit={(values, {setErrors}) => {
					if (submitting) return
					setSubmitting(true)
					saveSvgAsPngBlob(posterRef.current)
						.catch(() => null)
						.then((poster) =>
							fetch(endpoint, {
								method: 'post',
								body: jsonToFormData({
									...serializeValues(
										{
											...values,
											date_end: values.date_end || values.date_start
										},
										[
											'date_start',
											'date_end',
											'organisator_date_of_birth',
											'contactperson_date_of_birth'
										],
										toServerDate
									),
									poster,
									category: params.inspiratie
								})
							})
						)
						.then((res) => {
							switch (res.status) {
								case 422:
									return res.json().then(setErrors)
								case 200:
									res.json().then(({next}) => history.push(next))
								default:
									console.error(res)
							}
						})
						.catch(console.error)
						.then(() => {
							setSubmitting(false)
						})
				}}
				initialValues={
					editing
						? mergeValues(
								initialValues,
								serializeValues(
									form,
									[
										'date_start',
										'date_end',
										'organisator_date_of_birth',
										'contactperson_date_of_birth'
									],
									fromServerDate
								)
						  )
						: initialValues
				}
			>
				<Form translate="yes">
					<div class={styles('cols')}>
						<div class={styles('cols-form')}>
							<ActionForm {...orderedForm} />
						</div>
						<div class={styles('cols-poster')}>
							<PosterForm
								posterRef={posterRef}
								editing={editing}
								totalAmount={totalAmount}
								exportUrl={exportUrl}
								{...orderedForm}
							/>
						</div>
					</div>
					<div class={styles('cols-submit')}>
						{editing ? (
							<span class={styles('cols-submit-cancel')}>
								<Theme.Link
									id="register-cancel"
									onClick={() => setCancelPopupState(true)}
								>
									Annuleer je actie
								</Theme.Link>
							</span>
						) : null}
						<ButtonPrimary
							as="button"
							type="submit"
							loading={submitting}
							id="register-submit"
						>
							{t.submit}
						</ButtonPrimary>
					</div>
					{editing && (
						<RegisterActionPopupCancel
							open={cancelPopupState}
							onClose={() => setCancelPopupState(false)}
							onConfirm={cancelAction}
							{...discard}
						/>
					)}
				</Form>
			</Formik>
		</Theme.Container>
	)
}
export const RegisterActionPopupCancel: React.FC<
	DiscardType & {
		open: boolean
		onClose: () => void
		onConfirm: (message: string) => void
	}
> = ({open, onClose, onConfirm, ...discard}) => {
	const [message, setMessage] = useState('')
	return (
		<Popup open={open} onClose={onClose}>
			<PopupContent>
				<div className={styles.popupcancel}>
					{discard.title && (
						<Theme.H3 class={styles.popupcancel('title').mod('green')}>
							{discard.title}
						</Theme.H3>
					)}
					{discard.intro && (
						<Theme.P className={styles.popupcancel('intro')}>
							{discard.intro}
						</Theme.P>
					)}
					{discard.label && (
						<Theme.P className={styles.popupcancel('label')}>
							{discard.label}
						</Theme.P>
					)}
					{discard.label_help && (
						<Theme.P className={styles.popupcancel('label_help')}>
							{discard.label_help}
						</Theme.P>
					)}
					<textarea
						placeholder={discard.placeholder}
						name="message"
						onChange={(e) => setMessage(e.currentTarget.value)}
						className={styles.popupcancel('message')}
					/>
					<div className={styles.popupcancel('buttons')}>
						<span class={styles.popupcancel('buttons-close')}>
							<Theme.Link onClick={onClose}>{discard.back_button}</Theme.Link>
						</span>
						<span class={styles.popupcancel('buttons-submit')}>
							<ButtonPrimary
								disabled={!message || message.length < 20}
								mod="redbg"
								onClick={() => onConfirm(message)}
							>
								{discard.confirm_button}
							</ButtonPrimary>
							<span
								class={styles.popupcancel('buttons-submit-progress')}
								style={{
									width: `${
										message.length
											? message.length >= 20
												? 100
												: (100 / 20) * message.length
											: 0
									}%`
								}}
							></span>
						</span>
					</div>
				</div>
			</PopupContent>
		</Popup>
	)
}

export const RegisterActionTitle: FunctionComponent = ({children}) => (
	<div class={styles('title')}>{children}</div>
)

export const RegisterActionSection: FunctionComponent<{
	mod?: Mod<'noborder'>
}> = ({mod, children}) => <div class={styles.section.mod(mod)}>{children}</div>
